// Reports → Staff (F-ANL-03, spec/17 R-07, R-10): per member, the revenue of what they rang up (the one revenue
// definition over the ledger entries they posted — wallet spends they took included), the top-ups they took (money in,
// not revenue), refunds, discounts, the sales they voided and the comps they granted. What no member did — play the
// session clock charged to a wallet, a kiosk order — is its own last row. Needs `reports:financial`.

import type { StaffReport } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, BarChart, ChartEmpty, StatCard, Table, type TableColumn } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { KpiFigure, ReportCard, ReportGrid, ReportKpis, ReportNote, ReportPage } from './report-kit';
import { type ReportScope, useStaffReport } from './queries';
import { csvMoney, ExportCsv, moneyShort, NO_DATA, TabEmpty, TabPending } from './tab-kit';

type Line = StaffReport['members'][number];

export function StaffTab({ scope }: { readonly scope: ReportScope }): ReactNode {
  const { t } = useTranslation('console');
  const report = useStaffReport(scope, true);
  if (report.data === undefined) return <TabPending query={report} unavailable={t('reports.unavailable')} forbidden={t('reports.notPermittedFinancial')} />;
  if (report.data.members.length === 0) return <TabEmpty title={t('reports.emptyTitle')} description={t('reports.staff.empty')} />;
  return <Staff report={report.data} />;
}

/** A count and a money figure in one cell: "1 · ₹160.00" — a number, never the word "One" (2026-10-02). */
export function countAndAmount(format: ReturnType<typeof useVenueFormatter>, count: number, amount: string): string {
  return `${format.number(count)} · ${amount}`;
}

function Staff({ report }: { readonly report: StaffReport }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const short = (amount: number) => moneyShort(format, amount, report.currency);
  const nameOf = (line: Line) => (line.memberId === null ? t('reports.staff.nobody') : (line.name ?? t('reports.staff.former')));
  const members = report.members.filter((line) => line.memberId !== null);
  const byRevenue = [...members].filter((line) => line.revenue.amount > 0).sort((a, b) => b.revenue.amount - a.revenue.amount);
  const byTopUps = [...members].filter((line) => line.topUps.amount > 0).sort((a, b) => b.topUps.amount - a.topUps.amount);
  const top = byRevenue[0];
  const voids = report.members.reduce((sum, line) => sum + line.voids, 0);
  const voided = report.members.reduce((sum, line) => sum + line.voidedValue.amount, 0);
  const comps = report.members.reduce((sum, line) => sum + line.comps, 0);
  const compSeconds = report.members.reduce((sum, line) => sum + line.compSeconds, 0);
  const avatar = (line: Line) => <Avatar name={nameOf(line)} size="sm" decorative />;

  const columns: readonly TableColumn<Line>[] = [
    {
      key: 'member',
      header: t('reports.staff.member'),
      cell: (row) => (
        <span className="console-rt-member">
          {row.memberId === null ? null : avatar(row)}
          <span className={row.memberId === null ? 'console-muted' : undefined}>{nameOf(row)}</span>
        </span>
      ),
    },
    { key: 'revenue', header: t('reports.kpi.revenue'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.revenue)}</span> },
    { key: 'topUps', header: t('reports.column.topUps'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.topUps)}</span> },
    { key: 'refunds', header: t('reports.kpi.refunds'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.refunds)}</span> },
    { key: 'discounts', header: t('reports.kpi.discounts'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.discounts)}</span> },
    {
      key: 'voids',
      header: t('reports.staff.voids'),
      numeric: true,
      cell: (row) => <span className="console-num">{row.voids === 0 ? format.number(0) : countAndAmount(format, row.voids, format.money(row.voidedValue))}</span>,
    },
    {
      key: 'comps',
      header: t('reports.staff.comps'),
      numeric: true,
      cell: (row) => (
        <span className="console-num">{row.comps === 0 ? format.number(0) : countAndAmount(format, row.comps, t('reports.staff.minutes', { minutes: format.number(Math.round(row.compSeconds / 60)) }))}</span>
      ),
    },
  ];
  return (
    <ReportPage>
      {report.partial ? <ReportNote>{t('reports.partial')}</ReportNote> : null}
      <ReportKpis label={t('reports.tab.staff')}>
        <StatCard label={t('reports.staff.topSeller')} value={<span className="console-rt-figure-name">{top === undefined ? NO_DATA : nameOf(top)}</span>} hint={top === undefined ? t('reports.noData') : t('reports.staff.topSellerHint', { amount: short(top.revenue.amount) })} />
        <StatCard label={t('reports.staff.members')} value={<KpiFigure value={members.length} format={(n) => format.number(n)} />} hint={t('reports.staff.membersHint')} />
        <StatCard
          label={t('reports.staff.voids')}
          value={<KpiFigure value={voids} format={(n) => format.number(n)} />}
          hint={voids === 0 ? t('reports.staff.noVoids') : t('reports.staff.voidedValue', { amount: format.money({ amount: voided, currency: report.currency }) })}
        />
        <StatCard
          label={t('reports.staff.comps')}
          value={<KpiFigure value={comps} format={(n) => format.number(n)} />}
          hint={comps === 0 ? t('reports.staff.noComps') : t('reports.staff.compTime', { time: format.duration(compSeconds) })}
        />
      </ReportKpis>

      <ReportGrid layout="even">
        <ReportCard title={t('reports.staff.revenueBy')} description={t('reports.staff.revenueByHint')}>
          {byRevenue.length === 0 ? (
            <ChartEmpty>{t('reports.staff.noRevenue')}</ChartEmpty>
          ) : (
            <BarChart
              label={t('reports.staff.revenueBy')}
              limit={6}
              rows={byRevenue.map((line) => ({ key: line.memberId ?? 'none', label: nameOf(line), leading: avatar(line), value: line.revenue.amount, display: format.money(line.revenue) }))}
            />
          )}
        </ReportCard>
        <ReportCard title={t('reports.staff.topUpsBy')} description={t('reports.staff.topUpsByHint')}>
          {byTopUps.length === 0 ? (
            <ChartEmpty>{t('reports.staff.noTopUps')}</ChartEmpty>
          ) : (
            <BarChart
              label={t('reports.staff.topUpsBy')}
              limit={6}
              rows={byTopUps.map((line) => ({ key: line.memberId ?? 'none', label: nameOf(line), leading: avatar(line), value: line.topUps.amount, display: format.money(line.topUps), tone: 5 }))}
            />
          )}
        </ReportCard>
      </ReportGrid>

      <ReportCard title={t('reports.staff.table')} description={t('reports.staff.tableHint')} flush>
        <Table caption={t('reports.staff.table')} captionHidden columns={columns} rows={report.members} rowKey={(row) => row.memberId ?? 'none'} empty={t('reports.staff.empty')} />
      </ReportCard>
      <div className="console-rt-actions">
        <ExportCsv
          fileName={`staff-${report.from}-${report.to}.csv`}
          header={['member', 'revenue', 'top_ups', 'refunds', 'discounts', 'voids', 'voided_value', 'comps', 'comp_minutes', 'currency']}
          rows={report.members.map((line) => [
            nameOf(line),
            csvMoney(line.revenue),
            csvMoney(line.topUps),
            csvMoney(line.refunds),
            csvMoney(line.discounts),
            line.voids,
            csvMoney(line.voidedValue),
            line.comps,
            Math.round(line.compSeconds / 60),
            report.currency,
          ])}
        />
      </div>
    </ReportPage>
  );
}
